import { Component, computed, inject, input, InputSignal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, Observable, of, switchMap } from 'rxjs';

import { Course, environment, Teacher } from 'src/index';
import { ConfigurationService, CoursesService, LoadingService, PopupService, TeachersService } from 'src/index/services.index';

@Component({
    selector: 'meta-teacher-detail',
    templateUrl: './teacher-detail.component.html',
    styleUrls: ['./teacher-detail.component.css']
})
export class TeacherDetailComponent {
    teachersService = inject(TeachersService);
    loadingService = inject(LoadingService);
    coursesService = inject(CoursesService);
    popupService = inject(PopupService);
    configurationService = inject(ConfigurationService);
    router = inject(Router);

    $teacher: InputSignal<Partial<Teacher>> = input.required({ alias: 'teacher' });
    $hideName: InputSignal<boolean> = input(false, { alias: 'hideName' });
    $coursesPreview: InputSignal<boolean> = input(true, { alias: 'coursesPreview' });

    coursesPreviewConfig = {
        ...environment.teachersModule?.coursesPreviewConfig,
    };

    teacherData$: Observable<Partial<Teacher> | undefined> = toObservable(this.$teacher).pipe(
        switchMap(teacher => {
            if (teacher.id) {
                return this.teachersService.getTeacher(teacher.id).pipe(
                    catchError(() => of(teacher)),
                );
            }
            return of(teacher);
        }),
    );

    $teacherData = toSignal(this.teacherData$);
    $teacherTitularCourses = computed(() => (this.$teacherData()?.titularCourses ?? []) as Course[]);
    $teacherSupportCourses = computed(() => (this.$teacherData()?.supportCourses ?? []) as Course[]);

    $courseToPreview = signal<Course | undefined>(undefined);

    startCourse(course: Course, urlToOpen?: string | void) {
        if (!!course.status) {
            this.goToCourse(course, urlToOpen);
        } else {
            this.loadingService.show();
            firstValueFrom(this.coursesService.startCourse(course))
                .then(() => {
                    this.$courseToPreview.set(undefined);
                    this.goToCourse(course, urlToOpen);
                })
                .catch((err) => this.popupService.error(err.message || this.configurationService.startCourseErrorMsg))
                .finally(() => this.loadingService.hide());
        }
    }

    goToCourse(course: Course, urlToOpen?: string | void) {
        if (urlToOpen) {
            return this.router.navigateByUrl(urlToOpen);
        }
        return this.coursesService.openCourse(course);
    }

    updateCourseToPreview(course: Course) {
        this.$courseToPreview.set({ ...course });
    }
}
